import { useEffect, useState, useRef } from "react";
import { Bell, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useMember, memberApi } from "@/contexts/MemberAuthContext";

export default function NotificationsBell() {
  const { member } = useMember();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    if (!member) return;
    try {
      const { data } = await memberApi.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    if (!member) return;
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAll = async () => {
    try {
      await memberApi.post("/notifications/mark-read");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  if (!member) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        className="relative text-white/60 hover:text-[#D4AF37] transition-colors p-1"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#D4AF37] text-black text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-lg shadow-2xl z-50" data-testid="notif-panel">
          <div className="flex justify-between items-center p-4 border-b border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all-read" className="text-[10px] text-white/50 hover:text-[#D4AF37]">Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-white/40 text-xs">All caught up.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((n) => (
                <NotifRow key={n.id} notif={n} onClose={() => setOpen(false)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotifRow({ notif, onClose }) {
  const icon = notif.type === "unlock" ? "✨" : notif.type === "badge" ? "🏆" : notif.type === "referral" ? "🤝" : notif.type === "vault_drop" ? "🔓" : "◆";
  const body = (
    <div className={`p-3 hover:bg-white/[0.03] transition-colors ${!notif.read ? "border-l-2 border-[#D4AF37]" : ""}`}>
      <div className="flex gap-3">
        <span className="text-lg">{icon}</span>
        <div className="flex-1">
          <p className="text-white text-xs font-medium">{notif.title}</p>
          {notif.message && <p className="text-white/50 text-[11px] mt-1 whitespace-pre-wrap break-words">{notif.message}</p>}
          <p className="text-white/30 text-[10px] mt-1">{new Date(notif.ts).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
  if (notif.cta_url) {
    return <Link to={notif.cta_url} onClick={onClose} data-testid={`notif-row-${notif.id}`}>{body}</Link>;
  }
  return <div data-testid={`notif-row-${notif.id}`}>{body}</div>;
}
